import { useEffect, useState } from "react";
import { Sparkles, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { thumb } from "@/lib/img";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

function Tile({ src, alt, style }) {
  const [ok, setOk] = useState(false);
  return (
    <div className="rounded-2xl overflow-hidden bg-[#f3efe9] shadow-sm relative h-full min-h-0" style={style}>
      {!ok && <div className="absolute inset-0 animate-pulse bg-gradient-to-br from-[#f3efe9] via-[#ebe4da] to-[#f3efe9]" />}
      <img loading="lazy" decoding="async" src={src} alt={alt} onLoad={() => setOk(true)} className={`absolute inset-0 w-full h-full object-cover object-center transition-opacity duration-500 hover:scale-[1.02] ${ok ? "opacity-100" : "opacity-0"}`} />
    </div>
  );
}

const ROWS = 3;
function useCols() {
  const get = () => (window.innerWidth >= 1024 ? 3 : window.innerWidth >= 640 ? 2 : 1);
  const [cols, setCols] = useState(get);
  useEffect(() => { const f = () => setCols(get()); window.addEventListener("resize", f); return () => window.removeEventListener("resize", f); }, []);
  return cols;
}
// Explicit 3-column layouts [col, colSpan, row, rowSpan] so every count fills the box exactly.
const L3 = {
  1: [[1, 3, 1, 1]],
  2: [[1, 2, 1, 1], [3, 1, 1, 1]],
  3: [[1, 1, 1, 1], [2, 1, 1, 1], [3, 1, 1, 1]],
  4: [[1, 1, 1, 1], [2, 1, 1, 1], [3, 1, 1, 2], [1, 2, 2, 1]],
  5: [[1, 1, 1, 1], [2, 1, 1, 1], [3, 1, 1, 2], [1, 1, 2, 1], [2, 1, 2, 1]],
  6: [[1, 1, 1, 1], [2, 1, 1, 1], [3, 1, 1, 2], [1, 1, 2, 2], [2, 1, 2, 1], [2, 2, 3, 1]],
  7: [[1, 1, 1, 1], [2, 1, 1, 1], [3, 1, 1, 2], [1, 1, 2, 2], [2, 1, 2, 1], [2, 1, 3, 1], [3, 1, 3, 1]],
};
function fitPhotos(photos, cols) {
  if (cols === 3) {
    const n = Math.min(photos.length, 7);
    const lay = L3[n] || [];
    const rows = Math.max(1, ...lay.map(([, , r, rs]) => r + rs - 1));
    return { shown: lay.map(([c, cs, r, rs], i) => ({ u: photos[i], i, style: { gridColumn: `${c} / span ${cs}`, gridRow: `${r} / span ${rs}` } })), rows };
  }
  const n = Math.min(photos.length, cols * ROWS);
  const rows = Math.max(1, Math.ceil(n / cols));
  const shown = photos.slice(0, n).map((u, i) => {
    const last = i === n - 1, rem = rows * cols - n;
    return { u, i, style: last && rem > 0 ? { gridColumn: `span ${1 + rem} / span ${1 + rem}` } : undefined };
  });
  return { shown, rows };
}

export default function RealWeddings() {
  const [data, setData] = useState(null);
  const [active, setActive] = useState(null);
  const [fade, setFade] = useState(false);
  const [viewAll, setViewAll] = useState(false);
  const cols = useCols();

  useEffect(() => {
    api.get("/settings/real-weddings").then((r) => {
      setData(r.data);
      const first = (r.data.tabs || []).find((t) => t.photos?.length) || r.data.tabs?.[0];
      setActive(first?.key || null);
    }).catch(() => {});
  }, []);

  const tabs = (data?.tabs || []).filter((t) => t.photos?.length);
  if (!data || (data.tabs || []).length === 0) return null;
  if (tabs.length === 0) {
    return (
      <section data-testid="real-weddings" className="max-w-7xl mx-auto px-6 lg:px-8 py-20">
        <div className="inline-flex items-center gap-2 rounded-full border border-[#ebe6e0] bg-white px-3 py-1 text-xs text-[#292724]"><Sparkles className="w-3.5 h-3.5 text-[#d4af37]" /> {data.eyebrow}</div>
        <h2 data-testid="real-weddings-title" className="font-serif text-3xl sm:text-4xl lg:text-5xl mt-4 text-[#292724]">{data.title}</h2>
        <div data-testid="real-weddings-empty" className="mt-6 text-sm text-[#8a837a]">Photos coming soon.</div>
      </section>
    );
  }
  const tab = tabs.find((t) => t.key === active) || tabs[0];

  const pick = (key) => {
    if (key === tab.key) return;
    setFade(true);
    setTimeout(() => { setActive(key); setFade(false); }, 180);
  };

  const { shown, rows } = fitPhotos(tab.photos, cols);
  const overflow = shown.length < tab.photos.length;
  const rowH = "clamp(140px, calc((100vh - 400px) / 3), 260px)";

  return (
    <section data-testid="real-weddings" className="max-w-7xl mx-auto px-6 lg:px-8 py-20">
      <div className="inline-flex items-center gap-2 rounded-full border border-[#ebe6e0] bg-white px-3 py-1 text-xs text-[#292724]"><Sparkles className="w-3.5 h-3.5 text-[#d4af37]" /> {data.eyebrow}</div>
      <h2 data-testid="real-weddings-title" className="font-serif text-3xl sm:text-4xl lg:text-5xl mt-4 text-[#292724]">{data.title}</h2>

      <div data-testid="real-weddings-grid" className={`mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 grid-flow-dense gap-4 overflow-hidden transition-all duration-300 ${fade ? "opacity-0 translate-y-1" : "opacity-100 translate-y-0"}`}
        style={{ gridTemplateRows: `repeat(${rows}, ${rowH})`, height: `calc(${rows} * ${rowH} + ${(rows - 1) * 16}px)` }}>
        {shown.map(({ u, i, style }) => <Tile key={`${tab.key}-${u}-${i}`} src={thumb(u, 800)} alt={`${tab.label} ${i + 1}`} style={style} />)}
      </div>

      <div className="mt-8 flex flex-wrap items-center justify-between gap-3">
        <div data-testid="real-weddings-tabs" className="flex gap-2 overflow-x-auto pb-1 -mx-6 px-6 lg:mx-0 lg:px-0">
          {tabs.map((t) => {
            const on = t.key === tab.key;
            return (
              <button key={t.key} data-testid={`real-weddings-tab-${t.key}`} onClick={() => pick(t.key)} aria-pressed={on}
                className={`shrink-0 px-5 py-2 rounded-full text-sm transition-colors ${on ? "bg-[#d4af37] text-[#292724] font-medium" : "text-[#8a837a] hover:text-[#292724] hover:bg-white"}`}>
                {t.label}
              </button>
            );
          })}
        </div>
        {overflow && (
          <button type="button" data-testid="real-weddings-view-all" onClick={() => setViewAll(true)}
            className="shrink-0 inline-flex items-center gap-1.5 rounded-full border border-[#292724] px-5 py-2 text-sm text-[#292724] hover:bg-[#292724] hover:text-white transition-colors">
            View all {tab.photos.length} <ArrowRight className="w-4 h-4" />
          </button>
        )}
      </div>

      <Dialog open={viewAll} onOpenChange={setViewAll}>
        <DialogContent data-testid="real-weddings-view-all-dialog" className="max-w-6xl max-h-[90vh] overflow-y-auto bg-[#faf7f2]">
          <DialogHeader><DialogTitle className="font-serif text-2xl">{data.title} · {tab.label}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
            {tab.photos.map((u, i) => (
              <div key={`${u}-${i}`} className="aspect-[4/3] rounded-2xl overflow-hidden bg-[#f3efe9]">
                <img src={thumb(u, 800)} alt={`${tab.label} ${i + 1}`} className="w-full h-full object-cover object-center" loading="lazy" />
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
}
